import { PlaygroundParameters } from "./playground-parameters";
import { toast } from "sonner";
import { SubmitHandler } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import { ChatFormData, ChatInputProps, Model } from "@/types";
import { Mention } from "@/types/agent-tools";
import { usePlaygroundParametersStore } from "@/atoms/playground";
import {
  EditableContent,
  EditableContentRef,
} from "./agent-tools/editable-content-test";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDocuments, uploadDocument, deleteDocument } from "@/utils/axios-v2";
import { IDefaultModel, IDocument } from "@/types/conversations";
import { Paperclip } from "lucide-react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ErrorIcon from "@mui/icons-material/Error";
import LoopIcon from "@mui/icons-material/Loop";
import DeleteIcon from "@mui/icons-material/Clear";
import { Tooltip } from "@/components/ui/tooltip-card";
import { IconButton, useTheme } from "@mui/material";
import { useStoredTheme } from "@/utils/theme-context";
import { llmApi } from "@/services/rag-service";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

const MAX_UPLOAD_FILES = 5;
const MAX_UPLOAD_FILE_SIZE = 20 * 1024 * 1024;
const ACCEPTED_UPLOAD_TYPES =
  "image/*,application/pdf,text/plain,text/markdown,.md,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type LocalUploadState = "uploading" | "processing" | "ready" | "failed";

type LocalUploadItem = {
  id: string;
  filename: string;
  fileSize: number;
  contentType: string;
  state: LocalUploadState;
  error?: string;
};

const getUploadErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object") {
    const maybeAxiosError = error as {
      response?: { data?: { detail?: string; message?: string } };
      message?: string;
    };

    return (
      maybeAxiosError.response?.data?.detail ||
      maybeAxiosError.response?.data?.message ||
      maybeAxiosError.message ||
      fallback
    );
  }

  return fallback;
};

const getFileExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
};

const isAcceptedFileType = (file: File) => {
  return ACCEPTED_UPLOAD_TYPES.split(",").some((accept) => {
    const acceptedType = accept.trim().toLowerCase();

    if (!acceptedType) return false;
    if (acceptedType.startsWith(".")) {
      return getFileExtension(file.name) === acceptedType;
    }
    if (acceptedType.endsWith("/*")) {
      return file.type
        .toLowerCase()
        .startsWith(acceptedType.replace("/*", "/"));
    }

    return file.type.toLowerCase() === acceptedType;
  });
};

const normalizeFileName = (name: string) => name.trim().toLowerCase();

const normalizeFileType = (type: string | null | undefined) =>
  (type || "").trim().toLowerCase();

const isDuplicateDocumentFile = (document: IDocument, file: File) => {
  const sameName = normalizeFileName(document.name) === normalizeFileName(file.name);
  if (!sameName) return false;

  const documentType = normalizeFileType(
    document.content_type || document.file_type,
  );
  const fileType = normalizeFileType(file.type);
  const sameKnownType = Boolean(documentType && fileType && documentType === fileType);

  if (document.file_size == null) {
    return sameKnownType || !documentType || !fileType;
  }

  return Number(document.file_size) === file.size && sameKnownType;
};

const isDuplicateLocalUploadFile = (upload: LocalUploadItem, file: File) => {
  const sameName =
    normalizeFileName(upload.filename) === normalizeFileName(file.name);
  const sameSize = upload.fileSize === file.size;
  const uploadType = normalizeFileType(upload.contentType);
  const fileType = normalizeFileType(file.type);
  const sameType = uploadType === fileType;

  return sameName && sameSize && sameType;
};

const mapDocumentStatus = (status: string): LocalUploadState => {
  if (status === "complete") return "ready";
  if (status === "error") return "failed";
  if (status === "uploading") return "uploading";
  return "processing";
};

const normalizeDocuments = (data: unknown): IDocument[] => {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    const response = data as {
      documents?: unknown;
      data?: unknown;
      results?: unknown;
      items?: unknown;
    };

    if (Array.isArray(response.documents)) return response.documents;
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.results)) return response.results;
    if (Array.isArray(response.items)) return response.items;
  }

  return [];
};

export default function ChatInput({
  onSubmit,
  formData,
  conversationId,
  isLoading,
  stop,
  ...props
}: ChatInputProps & { stop: () => void }) {
  const { t } = useTranslation();
  const {
    data: defaultSelectedModels,
    isLoading: isLoadingDefaultSelectedModels,
  } = useQuery<{ defaults: IDefaultModel[] }>({
    queryKey: ["default-models"],
    queryFn: llmApi.getDefaultModels,
  });
  const storedTheme = useStoredTheme();
  const theme = useTheme();
  const [models, setModels] = useState<Model[]>([]);
  const [isExternalUploading, setIsExternalUploading] = useState(false);
  const [localUploads, setLocalUploads] = useState<LocalUploadItem[]>([]);
  const [deletingDocumentIds, setDeletingDocumentIds] = useState<string[]>([]);
  const {
    setLlmConfigId,
    setPlannerLlmConfig,
    setMode,
    setTemperature,
    setMaxTokens,
    setLanguage,
  } = usePlaygroundParametersStore();

  const {
    handleSubmit,
    formState: { errors },
    resetField,
    getValues,
    watch,
    setValue,
  } = formData;

  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editableContentRef = useRef<EditableContentRef>(null);

  const { data: documents = [] } = useQuery({
    queryKey: ["documents", conversationId],
    queryFn: () => getDocuments(conversationId!),
    refetchInterval: (query) => {
      const queryDocuments = normalizeDocuments(query.state.data);
      return queryDocuments.find(
        (doc) => doc.status != "complete" && doc.status != "error",
      )
        ? 5000
        : false;
    },
    select: normalizeDocuments,
    enabled: !!conversationId,
  });

  useEffect(() => {
    if (!documents?.length) return;

    const documentNames = new Set(documents.map((doc) => doc.name));
    setLocalUploads((current) =>
      current.filter(
        (upload) =>
          upload.state === "failed" || !documentNames.has(upload.filename),
      ),
    );
  }, [documents]);

  const { mutate: deleteFile } = useMutation({
    mutationFn: (document_id: string) =>
      deleteDocument(conversationId!, document_id),
    onMutate: (documentId) => {
      setDeletingDocumentIds((current) =>
        current.includes(documentId) ? current : [...current, documentId],
      );
    },
    onSuccess: (_data, documentId) => {
      editableContentRef.current?.removeDocumentMention(documentId);
      queryClient.setQueryData(["documents", conversationId], (current) => {
        const normalizedDocuments = normalizeDocuments(current);
        return normalizedDocuments.filter((doc) => doc.id !== documentId);
      });
      queryClient.invalidateQueries({
        queryKey: ["documents", conversationId],
      });
    },
    onError: (error) => {
      console.error("Error deleting document:", error);
      toast.error(
        getUploadErrorMessage(error, t("playground.failedToRemoveDocument")),
      );
    },
    onSettled: (_data, _error, documentId) => {
      setDeletingDocumentIds((current) =>
        current.filter((id) => id !== documentId),
      );
    },
  });

  const updateLocalUpload = (
    id: string,
    updates: Partial<Omit<LocalUploadItem, "id" | "filename">>,
  ) => {
    setLocalUploads((current) =>
      current.map((upload) =>
        upload.id === id ? { ...upload, ...updates } : upload,
      ),
    );
  };

  const removeLocalUpload = (id: string) => {
    setLocalUploads((current) =>
      current.filter((upload) => upload.id !== id),
    );
  };

  const uploadFilesToConversation = async (
    targetConversationId: string,
    uploadRequests: Array<{ file: File; upload: LocalUploadItem }>,
  ) => {
    await Promise.all(
      uploadRequests.map(async ({ file, upload }) => {
        try {
          updateLocalUpload(upload.id, { state: "uploading", error: undefined });
          await uploadDocument(targetConversationId, file);
          updateLocalUpload(upload.id, { state: "processing" });
          queryClient.invalidateQueries({
            queryKey: ["documents", targetConversationId],
          });
        } catch (error) {
          updateLocalUpload(upload.id, {
            state: "failed",
            error: getUploadErrorMessage(
              error,
              t("playground.failedToUploadDocument"),
            ),
          });
        }
      }),
    );

    queryClient.invalidateQueries({
      queryKey: ["documents", targetConversationId],
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);

    if (selectedFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    if (selectedFiles.length > MAX_UPLOAD_FILES) {
      toast.error(t("playground.fileUploadLimitExceeded"));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const attachedCount =
      (documents?.length || 0) +
      localUploads.filter((upload) => upload.state !== "failed").length;

    const uploadRequests: Array<{ file: File; upload: LocalUploadItem }> = [];
    const nextUploads: LocalUploadItem[] = [];
    const attachedDocuments = [...(documents || [])];
    const activeLocalUploads = localUploads.filter(
      (upload) => upload.state !== "failed",
    );

    selectedFiles.forEach((file) => {
      const uploadId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;

      const isDuplicate =
        attachedDocuments.some((document) =>
          isDuplicateDocumentFile(document, file),
        ) ||
        activeLocalUploads.some((upload) =>
          isDuplicateLocalUploadFile(upload, file),
        ) ||
        uploadRequests.some(({ file: queuedFile }) => {
          return (
            normalizeFileName(queuedFile.name) === normalizeFileName(file.name) &&
            queuedFile.size === file.size &&
            normalizeFileType(queuedFile.type) === normalizeFileType(file.type)
          );
        });

      if (isDuplicate) {
        toast.error(
          t("playground.duplicateFileUpload", { fileName: file.name }),
        );
        return;
      }

      if (file.size > MAX_UPLOAD_FILE_SIZE) {
        nextUploads.push({
          id: uploadId,
          filename: file.name,
          fileSize: file.size,
          contentType: file.type,
          state: "failed",
          error: t("playground.fileSizeExceedsLimit"),
        });
        return;
      }

      if (!isAcceptedFileType(file)) {
        nextUploads.push({
          id: uploadId,
          filename: file.name,
          fileSize: file.size,
          contentType: file.type,
          state: "failed",
          error: t("playground.unsupportedFileType"),
        });
        return;
      }

      if (attachedCount + uploadRequests.length >= MAX_UPLOAD_FILES) {
        toast.error(t("playground.fileUploadLimitExceeded"));
        return;
      }

      const upload = {
        id: uploadId,
        filename: file.name,
        fileSize: file.size,
        contentType: file.type,
        state: "uploading",
      } satisfies LocalUploadItem;

      uploadRequests.push({ file, upload });
      nextUploads.push(upload);
    });

    setLocalUploads((current) => [...current, ...nextUploads]);

    if (uploadRequests.length > 0) {
      if (conversationId) {
        await uploadFilesToConversation(conversationId, uploadRequests);
      } else if (props.onFileUpload) {
        setIsExternalUploading(true);
        try {
          await props.onFileUpload(uploadRequests.map(({ file }) => file));
          uploadRequests.forEach(({ upload }) => {
            updateLocalUpload(upload.id, { state: "processing" });
          });
        } catch (error) {
          uploadRequests.forEach(({ upload }) => {
            updateLocalUpload(upload.id, {
              state: "failed",
              error: getUploadErrorMessage(
                error,
                t("playground.failedToUploadDocument"),
              ),
            });
          });
        } finally {
          setIsExternalUploading(false);
        }
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isFileUploading =
    isExternalUploading ||
    localUploads.some(
      (upload) => upload.state === "uploading" || upload.state === "processing",
    );
  const activeAttachmentCount =
    (documents?.length || 0) +
    localUploads.filter((upload) => upload.state !== "failed").length;

  const currentModel = watch("llm_config_id");
  const currentPlannerModel = watch("planner_llm_config");

  useEffect(() => {
    if (models.length === 0 || !defaultSelectedModels) {
      return;
    }

    const fallbackModel = models[0];
    const getDefaultModel = (mode: "base" | "planner") => {
      const defaultModelId = defaultSelectedModels.defaults.find(
        (model) => model.mode === mode,
      )?.llm_config.id;

      return (
        models.find((model) => model.id === defaultModelId) || fallbackModel
      );
    };

    const defaultBaseModel =
      getDefaultModel("base");
    const defaultPlannerModel = getDefaultModel("planner");
    const hasCurrentModel = models.some((model) => model.id === currentModel);
    const hasCurrentPlannerModel = models.some(
      (model) => model.id === currentPlannerModel,
    );

    if (!hasCurrentModel && defaultBaseModel) {
      setValue("llm_config_id", defaultBaseModel.id);
      setLlmConfigId(defaultBaseModel.id);
    }

    if (!hasCurrentPlannerModel && defaultPlannerModel) {
      setValue("planner_llm_config", defaultPlannerModel.id);
      setPlannerLlmConfig(defaultPlannerModel.id);
    }
  }, [
    currentModel,
    currentPlannerModel,
    defaultSelectedModels,
    models,
    setLlmConfigId,
    setPlannerLlmConfig,
    setValue,
  ]);

  const submitMessage = (
    messageOverride?: string,
    mentionsOverride?: Mention[],
  ) => {
    const data = getValues();
    const payload = {
      ...data,
      message: messageOverride ?? data.message,
      metadata: {
        ...data.metadata,
        mentions: mentionsOverride ?? data.metadata?.mentions ?? [],
      },
    };

    onSubmit?.(payload, []);
    resetField("message");
  };

  const onFormSubmit: SubmitHandler<ChatFormData> = (data) => {
    onSubmit?.(data, []);
    resetField("message");
  };
  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      className="w-full max-w-[1000px] flex flex-col rounded-lg p-2 mx-auto gap-2"
      style={{
        backgroundColor:
          storedTheme.general.backgroundColor[theme.palette.mode],
      }}
    >
      {(documents && documents.length > 0) || localUploads.length > 0 ? (
        <div className="flex flex-wrap gap-2 px-2">
          {documents?.map((doc) => {
            const documentState = mapDocumentStatus(doc.status);
            const isRemovingDocument = deletingDocumentIds.includes(doc.id);

            return (
              <Tooltip
                key={doc.id}
                content={
                  documentState === "failed"
                    ? doc.error_message || t("playground.failedToUploadDocument")
                    : t("playground.typeAtToMention")
                }
              >
                <div
                  style={{
                    display: "flex",
                    maxWidth: "156px",
                    minHeight: "24px",
                    padding: "3px 8px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "8px",
                    background: "var(--color-103)",
                    cursor: "help",
                  }}
                >
                  {documentState === "failed" && !isRemovingDocument && (
                    <ErrorIcon
                      sx={{ fontSize: 16, color: "var(--color-104)" }}
                    />
                  )}
                  {documentState === "ready" && !isRemovingDocument && (
                    <AttachFileIcon
                      sx={{ fontSize: 16, color: "var(--color-99)" }}
                    />
                  )}
                  {(isRemovingDocument ||
                    documentState === "uploading" ||
                    documentState === "processing") && (
                    <LoopIcon
                      sx={{
                        fontSize: 16,
                        color: "var(--color-99)",
                        animation: "spin 2s linear infinite",
                      }}
                    />
                  )}
                  <span className="text-xs text-white truncate">{doc.name}</span>
                  <span className="text-[10px] text-gray-300">
                    {isRemovingDocument
                      ? t("common.removing")
                      : t(`playground.fileUploadStates.${documentState}`)}
                  </span>
                  <IconButton
                    disabled={isRemovingDocument}
                    aria-label={t("common.delete")}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent tooltip from interfering if needed, though usually fine
                      if (isRemovingDocument) return;
                      deleteFile(doc.id);
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              </Tooltip>
            );
          })}
          {localUploads.map((upload) => (
            <Tooltip
              key={upload.id}
              content={
                upload.state === "failed"
                  ? upload.error || t("playground.failedToUploadDocument")
                  : t(`playground.fileUploadStates.${upload.state}`)
              }
            >
              <div
                style={{
                  display: "flex",
                  maxWidth: "220px",
                  minHeight: "24px",
                  padding: "3px 8px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "6px",
                  borderRadius: "8px",
                  background: "var(--color-103)",
                  cursor: upload.state === "failed" ? "help" : "default",
                }}
              >
                {upload.state === "failed" && (
                  <ErrorIcon sx={{ fontSize: 16, color: "var(--color-104)" }} />
                )}
                {upload.state === "ready" && (
                  <AttachFileIcon
                    sx={{ fontSize: 16, color: "var(--color-99)" }}
                  />
                )}
                {(upload.state === "uploading" ||
                  upload.state === "processing") && (
                  <LoopIcon
                    sx={{
                      fontSize: 16,
                      color: "var(--color-99)",
                      animation: "spin 2s linear infinite",
                    }}
                  />
                )}
                <span className="text-xs text-white truncate">
                  {upload.filename}
                </span>
                <span className="text-[10px] text-gray-300">
                  {t(`playground.fileUploadStates.${upload.state}`)}
                </span>
                {upload.state === "failed" && (
                  <IconButton
                    aria-label={t("common.delete")}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeLocalUpload(upload.id);
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </div>
            </Tooltip>
          ))}
          {documents && documents.length > 0 && (
            <div className="flex items-center ml-1 rtl:ml-0 rtl:mr-1">
              <span className="text-[10px] text-gray-500 italic select-none">
                {t("playground.typeAtToMention")}
              </span>
            </div>
          )}
        </div>
      ) : null}

      <div className="relative">
        <EditableContent
          ref={editableContentRef}
          conversationId={conversationId}
          onTextChange={(message) => {
            setValue("message", message);
          }}
          handleFormSubmit={(messageOverride, mentionsOverride) => {
            submitMessage(messageOverride, mentionsOverride);
          }}
          isStreaming={isLoading}
          isUploading={isFileUploading}
          documents={documents}
          stop={stop}
        />

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
          accept={ACCEPTED_UPLOAD_TYPES}
          multiple
        />
      </div>

      {errors.message && (
        <p className="text-red-400 text-sm mt-1 px-1">
          {errors.message.message}
        </p>
      )}

      <PlaygroundParameters
        leftActions={
          <Tooltip content={t("playground.uploadDocumentTooltip")}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isFileUploading || activeAttachmentCount >= 5}
              className={`
            p-2 rounded-full
            text-gray-400 hover:text-foreground hover:bg-foreground/10
            transition-all duration-200
            ${isFileUploading || activeAttachmentCount >= 5 ? "opacity-50 cursor-not-allowed" : ""}
          `}
            >
              <Paperclip size={20} />
            </button>
          </Tooltip>
        }
        onModeChange={(mode) => {
          setValue("mode", mode);
          setMode(mode);
        }}
        mode={watch("mode")}
        selectedModel={watch("llm_config_id")}
        selectedPlannerModel={watch("planner_llm_config")}
        temperature={watch("temperature") || 0}
        maxTokens={watch("max_tokens") || 0}
        language={watch("metadata.language") || i18n.language || "en"}
        onModelChange={(modelId) => {
          setValue("llm_config_id", modelId);
          setLlmConfigId(modelId);
        }}
        onPlannerModelChange={(modelId) => {
          setValue("planner_llm_config", modelId);
          setPlannerLlmConfig(modelId);
        }}
        onTemperatureChange={(value) => {
          setValue("temperature", value);
          setTemperature(value);
        }}
        onMaxTokensChange={(value) => {
          setValue("max_tokens", value);
          setMaxTokens(value);
        }}
        onLanguageChange={(language) => {
          setValue("metadata.language", language);
          setLanguage(language);
        }}
        onModelsLoaded={setModels}
        onOpenToolsMenu={(rect) => editableContentRef.current?.openMenu(rect)}
        defaultModels={defaultSelectedModels?.defaults}
      />
    </form>
  );
}
